import type { JSX } from 'react'
import { Button } from 'primereact/button'
import ChannelProviderCard from './ChannelProviderCard.js'
import AccountsListTable from './AccountsListTable.js'
import { FormSection, InfoCard } from '../../forms/layout/index.js'
import type { ChannelProviderName, ConnectedAccount } from '../../../pages/admin/channels-types.js'

export interface OAuthChannelsPanelProps {
  provider: ChannelProviderName
  accounts: ConnectedAccount[]
  accountsLoading: boolean
  onCardComplete: () => Promise<void>
  onPendingAssetSelection: (provider: string, assets: Array<{ id: string; name: string }>) => void
  onDisconnectAccount: (account: ConnectedAccount) => Promise<void>
  onReconnectAccount: (account: ConnectedAccount) => void
  onAddAccount: () => void
  onViewAccount: (account: ConnectedAccount) => void
  onEditAccount: (account: ConnectedAccount) => void
  onDeleteAccount: (account: ConnectedAccount) => Promise<void>
}

/**
 * OAuth channel-provider panel: the connect-prompt card (only when this
 * provider has zero connected accounts) plus the connected-accounts table.
 * Once at least one account exists, the table + "Add Account" button are
 * the only way to manage accounts — no per-account card is rendered here,
 * since the table already lists every connected account for this provider.
 * Sibling to TwilioChannelsPanel; extracted from ChannelsPage's former
 * OAuth-vs-Twilio ternary.
 */
export default function OAuthChannelsPanel(props: OAuthChannelsPanelProps): JSX.Element {
  const { provider, accounts } = props
  const hasAccounts = accounts.length > 0

  return (
    <>
      <InfoCard icon="pi pi-info-circle" tone="info" title="OAuth Channels">
        Securely connect your social accounts to manage messages, comments, and content from one place.
      </InfoCard>

      {/* Connect prompt — only when this provider has no connected accounts yet */}
      {!hasAccounts && (
        <ChannelProviderCard
          provider={provider}
          asset={null}
          onComplete={props.onCardComplete}
          onPendingAssetSelection={(a) => props.onPendingAssetSelection(provider, a)}
        />
      )}

      <FormSection
        icon="pi pi-users"
        eyebrow="ACCOUNTS"
        title="Connected Accounts"
        description="Manage the accounts connected for this channel."
        actions={
          <Button
            label="Add Account"
            icon="pi pi-plus"
            onClick={props.onAddAccount}
          />
        }
        bodyClassName="p-0"
      >
        <AccountsListTable
          accounts={props.accounts}
          loading={props.accountsLoading}
          onDisconnect={props.onDisconnectAccount}
          onReconnect={props.onReconnectAccount}
          onView={props.onViewAccount}
          onEdit={props.onEditAccount}
          onDelete={props.onDeleteAccount}
        />
      </FormSection>

      <InfoCard icon="pi pi-lightbulb" tone="default">
        After connecting, you can select specific pages or accounts to manage.
      </InfoCard>
    </>
  )
}
